/**
 * Product Manager
 *
 * Sistema centralizado para gerenciamento de interações com cards de produto.
 * Gerencia popups de tamanho, linha de cores, e futuras funcionalidades.
 *
 * @version 1.0.0
 * @author Wicomm
 *
 * Arquitetura:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                     ProductManager                              │
 * │                  (Single Responsibility)                        │
 * ├─────────────────────────────────────────────────────────────────┤
 * │  - Gerencia popup de tamanhos                                   │
 * │  - Gerencia linha de cores (productCard)                  │
 * │  - Usa delegação de eventos (funciona com cards dinâmicos)      │
 * │  - Extensível para: quick view, wishlist, compare, etc.         │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Cards suportados:
 * - .productCardMinimal (product-card.liquid)
 * - .productCard (product-card.liquid e JS-rendered)
 *
 * Princípios SOLID aplicados:
 * - SRP: Apenas gerencia interações de cards de produto
 * - OCP: Extensível via novos métodos, fechado para modificação
 * - DIP: Depende de data attributes, não de implementações específicas
 */

/* ==========================================================================
   PRODUCT MANAGER
   Gerenciamento de interações com cards de produto
   ========================================================================== */

class ProductManager {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.cardSelector - Seletor dos cards
   * @param {string} config.popupSelector - Seletor do popup de tamanhos
   * @param {string} config.toggleSelector - Seletor do botão toggle
   * @param {string} config.colorsSelector - Seletor da linha de cores
   * @param {string} config.iconSelector - Seletor do ícone do botão
   */
  constructor(config = {}) {
    this.config = {
      cardSelector: '.productCardMinimal, .productCard',
      popupSelector: '[data-size-popup]',
      toggleSelector: '[data-product-add-toggle]',
      colorsSelector: '[data-colors-row]',
      sizeOptionSelector: '.productCardMinimal__sizeOption, .productCard__sizeOption',
      iconSelector: 'svg',
      ...config
    };

    this._init();
  }

  /**
   * Inicialização
   * @private
   */
  _init() {
    this._bindPopupEvents();
  }

  /**
   * Vincula eventos de popup via delegação
   * @private
   */
  _bindPopupEvents() {
    document.addEventListener('click', (e) => {
      const toggleButton = e.target.closest(this.config.toggleSelector);

      if (toggleButton) {
        e.preventDefault();
        e.stopPropagation();

        const card = toggleButton.closest(this.config.cardSelector);
        if (!card) return;

        const popup = card.querySelector(this.config.popupSelector);
        if (!popup) return;

        // Toggle popup
        if (popup.classList.contains('opacity-0')) {
          this.openPopup(card);
        } else {
          this.closePopup(card);
        }
        return;
      }

      // Fechar popup ao clicar fora
      const isInsidePopup = e.target.closest(this.config.popupSelector);
      const isToggleButton = e.target.closest(this.config.toggleSelector);

      if (!isInsidePopup && !isToggleButton) {
        this.closeAllPopups();
      }

      // Adicionar ao carrinho ao clicar no tamanho
      const sizeOption = e.target.closest(this.config.sizeOptionSelector);
      if (sizeOption) {
        this._handleSizeSelection(sizeOption);
      }
    });
  }

  /**
   * Gerencia a seleção de tamanho e adição ao carrinho
   * @private
   * @param {HTMLElement} sizeOption - O botão de tamanho clicado
   */
  async _handleSizeSelection(sizeOption) {
    const variantId = sizeOption.dataset.variantId;
    if (!variantId) return;

    const card = sizeOption.closest(this.config.cardSelector);

    // Congela dimensões do botão antes do loading para que o SVG injetado
    // pelo CartManager não expanda o botão e afete o layout do card.
    const rect = sizeOption.getBoundingClientRect();
    sizeOption.style.width = rect.width + 'px';
    sizeOption.style.height = rect.height + 'px';
    sizeOption.style.overflow = 'hidden';
    sizeOption.style.display = 'inline-flex';
    sizeOption.style.alignItems = 'center';
    sizeOption.style.justifyContent = 'center';
    sizeOption.style.flexShrink = '0';

    // Redimensiona qualquer SVG injetado (spinner, sucesso, erro) para caber
    // no espaço natural do botão, mantendo proporção quadrada.
    const iconDim = Math.min(rect.width, rect.height);
    const resizeInjectedSvg = () => {
      const svg = sizeOption.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', iconDim);
        svg.setAttribute('height', iconDim);
      }
    };

    const observer = new MutationObserver(resizeInjectedSvg);
    observer.observe(sizeOption, { childList: true });

    try {
      const result = await window.CartManager.addToCart(variantId, {
        button: sizeOption
      });

      if (result.success) {
        // O CartDrawer abre automaticamente via evento cart:added emitido pelo CartManager
        if (card) this.closePopup(card);
        window.SearchDrawerManager?.close();
      }
    } catch (error) {
      console.error('[ProductManager] Error adding to cart:', error);
    } finally {
      // Para de observar e libera as dimensões fixas
      observer.disconnect();
      sizeOption.style.width = '';
      sizeOption.style.height = '';
      sizeOption.style.overflow = '';
      sizeOption.style.display = '';
      sizeOption.style.alignItems = '';
      sizeOption.style.justifyContent = '';
      sizeOption.style.flexShrink = '';
    }
  }

  /* ==========================================================================
     POPUP DE TAMANHOS
     ========================================================================== */

  /**
   * Abre o popup de tamanhos de um card
   * @param {HTMLElement} card - Elemento do card
   */
  openPopup(card) {
    if (!card) return;

    // Fecha qualquer outro popup que esteja aberto antes de abrir o atual
    this.closeAllPopups(card);

    const popup = card.querySelector(this.config.popupSelector);
    const icon = card.querySelector(`${this.config.toggleSelector} ${this.config.iconSelector}`);

    if (!popup) return;

    // Marca card como ativo (para CSS manter elementos visíveis)
    card.classList.add('popup-active');

    // Anima popup
    popup.classList.remove('opacity-0', 'invisible', 'translate-y-[0.5rem]');
    popup.classList.add('opacity-100', 'visible', 'translate-y-0');

    // Rotaciona ícone para X
    if (icon) icon.classList.add('rotate-45');

    // Mostra linha de cores (apenas productCard)
    this.showColorsRow(card);
  }

  /**
   * Fecha o popup de tamanhos de um card
   * @param {HTMLElement} card - Elemento do card
   */
  closePopup(card) {
    if (!card) return;

    const popup = card.querySelector(this.config.popupSelector);
    const icon = card.querySelector(`${this.config.toggleSelector} ${this.config.iconSelector}`);

    if (!popup) return;

    // Remove marca de ativo
    card.classList.remove('popup-active');

    // Anima popup
    popup.classList.add('opacity-0', 'invisible', 'translate-y-[0.5rem]');
    popup.classList.remove('opacity-100', 'visible', 'translate-y-0');

    // Reseta ícone
    if (icon) icon.classList.remove('rotate-45');

    // Esconde linha de cores
    this.hideColorsRow(card);
  }

  /**
   * Fecha todos os popups abertos
   * @param {HTMLElement|null} [excludeCard] - Card a ser excluído do fechamento
   */
  closeAllPopups(excludeCard = null) {
    const openPopups = document.querySelectorAll(`${this.config.popupSelector}.opacity-100`);

    openPopups.forEach((popup) => {
      const card = popup.closest(this.config.cardSelector);
      if (card && card !== excludeCard) {
        this.closePopup(card);
      }
    });
  }

  /* ==========================================================================
     LINHA DE CORES (productCard)
     ========================================================================== */

  /**
   * Mostra a linha de cores
   * Apenas para productCard
   * @param {HTMLElement} card - Elemento do card
   */
  showColorsRow(card) {
    if (!card || !card.classList.contains('productCard')) return;

    card.querySelectorAll('[data-colors-row], [data-related-colors-row]').forEach((row) => {
      row.classList.remove('opacity-0', 'invisible');
      row.classList.add('opacity-100', 'visible');
      row.style.maxHeight = '2rem';
    });
  }

  /**
   * Esconde a linha de cores
   * Apenas para productCard
   * @param {HTMLElement} card - Elemento do card
   */
  hideColorsRow(card) {
    if (!card || !card.classList.contains('productCard')) return;

    card.querySelectorAll('[data-colors-row], [data-related-colors-row]').forEach((row) => {
      row.classList.add('opacity-0', 'invisible');
      row.classList.remove('opacity-100', 'visible');
      row.style.maxHeight = '0';
    });
  }

  /* ==========================================================================
     FUTURAS FUNCIONALIDADES
     ========================================================================== */

  // /**
  //  * Abre quick view de um produto
  //  * @param {string} productHandle - Handle do produto
  //  */
  // async openQuickView(productHandle) {
  //   // TODO: Implementar quick view
  // }

  // /**
  //  * Adiciona/remove produto da wishlist
  //  * @param {string} productId - ID do produto
  //  */
  // toggleWishlist(productId) {
  //   // TODO: Implementar wishlist
  // }

  /**
   * Destroi a instância
   */
  destroy() {
    // Remove event listeners se necessário
  }
}

/* ==========================================================================
   INICIALIZAÇÃO
   ========================================================================== */

// Aguarda DOM e inicializa
function initProductManager() {
  window.ProductManager = new ProductManager();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductManager);
} else {
  initProductManager();
}

// Suporte ao Theme Editor
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Reinicializa se necessário
    if (!window.ProductManager) {
      initProductManager();
    }
  });
}

// Export para módulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ProductManager };
}